import { useState } from 'react'
import i18n from '@/i18n/index.js'

/**
 * Language management: read/write localStorage and toggle dir/lang on
 * documentElement. Same shape as theme.ts on purpose — English is the
 * default, so only Arabic has to be opted into, and index.html applies the
 * stored choice before first paint so a reload never flashes the wrong
 * direction (LTR content briefly shown RTL, or vice versa).
 */

export const SUPPORTED_LANGUAGES = ['en', 'ar'] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]

const STORAGE_KEY = 'language'

export const LANGUAGE_DIR: Record<Language, 'ltr' | 'rtl'> = {
  en: 'ltr',
  ar: 'rtl',
}

/** Read the persisted language from localStorage, or 'en' if not set. */
export function getStoredLanguage(): Language {
  const stored = localStorage.getItem(STORAGE_KEY)
  return stored === 'ar' ? 'ar' : 'en'
}

/** Apply a language: set dir/lang on <html>, update localStorage, switch i18next. */
export function applyLanguage(language: Language): void {
  document.documentElement.lang = language
  document.documentElement.dir = LANGUAGE_DIR[language]
  localStorage.setItem(STORAGE_KEY, language)
  void i18n.changeLanguage(language)
}

/**
 * Hook to read and set the current language. The initial value is already
 * correct on mount — index.html sets dir/lang before first paint, and
 * i18n/index.ts initialises with the same stored value — so this only
 * needs to apply anything on an actual change.
 */
export function useLanguage(): { language: Language; setLanguage: (l: Language) => void } {
  const [language, setLanguageState] = useState<Language>(() => getStoredLanguage())

  const setLanguage = (next: Language) => {
    setLanguageState(next)
    applyLanguage(next)
  }

  return { language, setLanguage }
}
